import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BentoGallery = () => {
  const navigate = useNavigate();
  
  // Reusable Arrow Component
  const ArrowButton = () => (
    <div className="absolute top-6 right-6 z-20">
      <div className="w-12 h-12 bg-white/60 rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-accent transition-all">
        <ArrowUpRight className="w-5 h-5 text-neutral-600 group-hover:text-accent" />
      </div>
    </div>
  );

  // Uploading Soon Tag
  const UploadingTag = () => (
    <div className="mt-3 inline-block bg-white/60 backdrop-blur-md px-3 py-1 rounded-full border border-white/50 shadow-sm">
      <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">Updating soon</span>
    </div>
  );

  // --- Visual Components ---

  const AnalyticsVisual = () => (
    <div className="w-full h-full relative flex items-end justify-center px-6 pb-6">
      {/* Abstract Chart Container */}
      <motion.div 
        className="w-full aspect-[4/3] bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/40 relative overflow-hidden"
        variants={{
          idle: { y: 20, rotate: 0 },
          active: { y: 0, rotate: -2, transition: { type: "spring", stiffness: 200 } }
        }}
      >
        {/* Floating Data Bubbles */}
        <motion.div 
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-teal-400/20"
          variants={{ active: { scale: [1, 1.2, 1], transition: { repeat: Infinity, duration: 2 } } }}
        />
        
        {/* Bar Chart */}
        <div className="flex items-end justify-between h-full gap-2 mt-4">
          {[40, 70, 50, 90, 60].map((h, i) => (
            <motion.div 
              key={i}
              className="w-full bg-teal-500/80 rounded-t-lg opacity-80"
              style={{ height: `${h}%` }}
              variants={{
                idle: { height: `${h}%`, opacity: 0.5 },
                active: { height: `${h + 10}%`, opacity: 1, transition: { delay: i * 0.05 } }
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Floating Widget */}
      <motion.div 
        className="absolute bottom-10 -right-2 w-20 h-20 bg-white/90 backdrop-blur rounded-2xl shadow-md border border-white/50 flex items-center justify-center"
        variants={{
          idle: { x: 20, opacity: 0 },
          active: { x: 0, opacity: 1, rotate: 5, transition: { delay: 0.2 } }
        }}
      >
        <div className="w-10 h-10 rounded-full border-[3px] border-teal-500/30 border-t-teal-500 flex items-center justify-center text-[10px] font-bold text-teal-700">
          84%
        </div>
      </motion.div>
    </div>
  );

  const FoundationsVisual = () => (
    <div className="w-full h-full relative flex flex-col items-center justify-center pt-4">
      {/* Stacked Planes */}
      <div className="relative w-48 h-48">
        {[1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className="absolute inset-0 bg-white rounded-2xl shadow-sm border border-orange-100/50 flex flex-col p-4 gap-3"
            style={{ 
              zIndex: 3-i,
              top: i * 15,
              scale: 1 - (i * 0.05),
              opacity: 1 - (i * 0.1)
            }}
            variants={{
              idle: { y: 0, rotate: 0 },
              active: { 
                y: -i * 20, 
                rotate: i * 5 - 5,
                transition: { type: "spring", stiffness: 200 }
              }
            }}
          >
            {/* Mock UI Elements */}
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-400/40" />
              <div className="h-3 w-24 rounded-full bg-neutral-100" />
            </div>
            <div className="h-20 rounded-lg bg-orange-50 w-full" />
            <div className="h-2 rounded-full bg-neutral-100 w-1/2" />
          </motion.div>
        ))}
      </div>
    </div>
  );

  const MaturityVisual = () => (
    <div className="w-full h-full relative flex items-end px-6 pb-8">
      <div className="w-full flex items-end justify-between gap-3 relative z-10">
        {[1, 2, 3, 4].map((i) => (
          <motion.div
            key={i}
            className="w-full bg-purple-400/30 rounded-lg relative group-hover:bg-purple-500/40 transition-colors"
            style={{ height: i * 25 }}
            variants={{
              idle: { height: i * 25 },
              active: { height: i * 35, transition: { type: "spring", bounce: 0.4 } }
            }}
          >
            {/* Connector Dot */}
            <motion.div 
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-2 border-purple-400 rounded-full"
              variants={{
                active: { scale: 1.2, borderColor: "#A855F7" }
              }}
            />
          </motion.div>
        ))}
        
        {/* Connecting Line (SVG) */}
        <svg className="absolute top-0 left-0 w-full h-full -z-10 pointer-events-none overflow-visible">
           <motion.path 
             d="M 10 80 L 50 60 L 90 40 L 130 10" 
             fill="none" 
             stroke="#C084FC" 
             strokeWidth="2"
             strokeDasharray="5 5"
             variants={{
               active: { strokeDashoffset: [0, -20], transition: { repeat: Infinity, ease: "linear", duration: 1 } }
             }}
           />
        </svg>
      </div>
    </div>
  );

  // Updated card classes: removed p-8, added flex-col to manage text/visual separation
  const cardClasses = "snap-center shrink-0 w-[85vw] md:w-[400px] h-[450px] bg-white border border-neutral-100 rounded-[2rem] relative overflow-hidden group hover:brightness-95 transition-all flex flex-col justify-between cursor-pointer";

  return (
    <section className="pt-10 pb-20 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        <div className="flex flex-col gap-1 mb-12">
            <h2 className="text-3xl font-serif text-neutral-900">Narayana Health</h2>
            <span className="text-lg font-light text-accent">2021 - current</span>
        </div>

        {/* Horizontal Scroll Container */}
        <div className="relative w-screen left-1/2 -ml-[50vw]">
          <div 
            className="flex overflow-x-auto gap-6 pb-12 snap-x snap-mandatory scrollbar-hide px-6 md:px-[max(3rem,calc((100vw-72rem)/2+3rem))]"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* Card 0: NH Care - Image Edge-to-Edge with Navigation */}
            <motion.div 
              whileTap={{ scale: 0.98 }}
              className={cardClasses}
              onClick={() => navigate('/nh-care')}
            >
              <ArrowButton />
              <div className="p-8 pb-0 z-10 relative">
                <h3 className="text-xl font-bold text-neutral-900 pr-12">NH Care - Consultations, Tests & Checkups, Health Records.</h3>
              </div>
              <div className="w-full flex-1 mt-auto relative z-0">
                 <img 
                   src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/19v2frqv_NH%20Care.jpg"
                   alt="NH Care Logo"
                   className="w-full h-full object-cover object-center"
                 />
              </div>
            </motion.div>

            {/* Card 1: Medha Scribe */}
            <motion.div 
              initial="idle"
              whileHover="active"
              whileTap={{ scale: 0.98 }}
              className={cardClasses}
              onClick={() => navigate('/clinical-tool')}
            >
              <ArrowButton />
              <div className="p-8 pb-0 z-10 relative">
                <h3 className="text-xl font-bold text-neutral-900 pr-12">Medha Scribe - Ambient Clinical documentation tool</h3>
              </div>
              <div className="w-full flex-1 mt-auto relative z-0">
                 <img 
                   src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/2mu7qy9b_Scribe.jpg" 
                   alt="Medha Scribe Interface" 
                   className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" 
                 />
              </div>
            </motion.div>

            {/* Card 2: Analytics */}
            <motion.div 
              initial="idle"
              whileHover="active"
              whileTap={{ scale: 0.98 }}
              className={cardClasses}
              onClick={() => navigate('/analytics')}
            >
               <ArrowButton />
               <div className="p-8 pb-0 z-10 relative">
                   <h3 className="text-xl font-bold text-neutral-900 pr-12">Analytics & Insights Platform</h3>
                   <UploadingTag />
               </div>
               <div className="w-full flex-1 mt-4 relative z-0">
                 <AnalyticsVisual />
               </div>
            </motion.div>

            {/* Card 3: Design Foundations */}
            <motion.div 
              initial="idle"
              whileHover="active"
              whileTap={{ scale: 0.98 }}
              className={cardClasses}
              onClick={() => navigate('/foundations')}
            >
               <ArrowButton />
               <div className="p-8 pb-0 z-10 relative">
                   <h3 className="text-xl font-bold text-neutral-900 leading-tight pr-12">Building Enterprise Design System Foundations</h3>
                   <UploadingTag />
               </div>
               <div className="w-full flex-1 mt-4 relative z-0">
                 <FoundationsVisual />
               </div>
            </motion.div>

            {/* Card 4: Design Maturity */}
            <motion.div 
              initial="idle"
              whileHover="active"
              whileTap={{ scale: 0.98 }}
              className={cardClasses}
              onClick={() => navigate('/maturity')}
            >
               <ArrowButton />
               <div className="p-8 pb-0 z-10 relative">
                   <h3 className="text-xl font-bold text-neutral-900 leading-tight pr-12">Building Design Maturity</h3>
               </div>
               <div className="w-full flex-1 mt-4 relative z-0">
                 <MaturityVisual />
               </div>
            </motion.div>

            <div className="w-6 md:w-12 shrink-0" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BentoGallery;
